/** DeclaredAffiliatesPanel
 *
 * Lists every individual + org that self-declared this trade org at
 * signup (from `/api/orgs/{org_id}/declared-affiliates`). Committee
 * members can accept / reject each declaration in one click, which
 * flips a `Verified by <Trade Org>` chip on the target's public
 * profile.
 *
 * Also renders a live-count badge in the panel header
 * (`Members · 12 · 3 pending`) so the committee sees at a glance
 * what's outstanding.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, X, Undo2, ExternalLink, ShieldCheck, Clock, XCircle, Users, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function DeclaredAffiliatesPanel({ org, accent = "#F5B82E" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/orgs/${org.id}/declared-affiliates`);
      setData(r);
    } catch (err) {
      // Silently degrade: unknown org id / auth issue → just don't render
      if (err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error("Could not load declared affiliates");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { void load(); }, [load]);

  const vouch = async (kind, targetId, decision) => {
    const key = `${kind}:${targetId}`;
    setBusy((s) => new Set([...s, key]));
    try {
      const url = kind === "user"
        ? `/orgs/${org.id}/declared-affiliates/users/${targetId}/vouch`
        : `/orgs/${org.id}/declared-affiliates/orgs/${targetId}/vouch`;
      await api.post(url, { decision });
      toast.success(
        decision === "accept" ? "Vouched — member verified"
        : decision === "reject" ? "Rejected"
        : "Reset to pending"
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vouch failed");
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  if (loading && !data) {
    return null;   // don't flash while loading
  }
  if (!data) {
    return null;   // no permission / no data
  }

  const { individuals = [], orgs = [], counts = {} } = data;
  const totalDeclared = counts.individuals + counts.orgs;

  if (totalDeclared === 0) {
    return (
      <div className="cs-card rounded-2xl p-6" data-testid="declared-affiliates-panel-empty">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
          Declared Affiliates
        </div>
        <div className="mt-2 text-sm text-white/60">
          No members have declared this trade org at signup yet.
        </div>
        <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
          Individuals who pick your association during signup (via
          &lsquo;Verification method · Association&rsquo;) will land here for
          you to vouch. Studios / vendors / agencies that tick your code
          from their affiliations picker will also appear here automatically.
        </div>
      </div>
    );
  }

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="declared-affiliates-panel">
      {/* Header + live count badges */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
            Declared Affiliates
          </div>
          <div className="mt-1 text-sm text-white/80">
            {totalDeclared} member{totalDeclared === 1 ? "" : "s"} declared this trade org at signup.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[10px] tracking-[0.18em] uppercase">
          {counts.pending > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-amber-500/12 border border-amber-500/40 text-amber-200"
              data-testid="declared-affiliates-pending-badge">
              <Clock size={10} className="inline mr-1" />
              {counts.pending} pending
            </span>
          )}
          <span className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/15 text-white/70">
            <Users size={10} className="inline mr-1" />
            {counts.individuals} individuals
          </span>
          <span className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/15 text-white/70">
            <Building2 size={10} className="inline mr-1" />
            {counts.orgs} orgs
          </span>
        </div>
      </div>

      {/* Individuals */}
      {individuals.length > 0 && (
        <div className="mt-5" data-testid="declared-individuals-list">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">
            Individuals
          </div>
          <div className="space-y-2">
            {individuals.map((i) => (
              <AffiliateRow key={i.id} accent={accent}
                kind="user"
                target={i}
                subtitle={[i.designation || i.primary_role, i.city].filter(Boolean).join(" · ")}
                membership={i.membership_number}
                busy={busy.has(`user:${i.id}`)}
                onVouch={(d) => vouch("user", i.id, d)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Orgs */}
      {orgs.length > 0 && (
        <div className="mt-5" data-testid="declared-orgs-list">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">
            Organizations
          </div>
          <div className="space-y-2">
            {orgs.map((o) => (
              <AffiliateRow key={o.id} accent={accent}
                kind="org"
                target={{ id: o.id, name: o.brand_name, vouch_status: o.vouch_status }}
                subtitle={[o.type?.replace(/_/g, " "), o.city, o.code].filter(Boolean).join(" · ")}
                membership={o.membership_number}
                busy={busy.has(`org:${o.id}`)}
                onVouch={(d) => vouch("org", o.id, d)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AffiliateRow({ accent, kind, target, subtitle, membership, busy, onVouch }) {
  const status = target.vouch_status || "pending";
  const badge =
    status === "accepted" ? (
      <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 inline-flex items-center gap-1"
        data-testid={`vouch-badge-accepted-${target.id}`}>
        <ShieldCheck size={10}/> Verified
      </span>
    ) : status === "rejected" ? (
      <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-red-500/12 border border-red-500/40 text-red-200 inline-flex items-center gap-1"
        data-testid={`vouch-badge-rejected-${target.id}`}>
        <XCircle size={10}/> Rejected
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-amber-500/12 border border-amber-500/40 text-amber-200 inline-flex items-center gap-1"
        data-testid={`vouch-badge-pending-${target.id}`}>
        <Clock size={10}/> Pending
      </span>
    );

  const profileHref =
    kind === "user"
      ? `/u/${target.id}`
      : `/directory/orgs?type=studio_infra&q=${encodeURIComponent(target.name || "")}`;

  return (
    <div className="rounded-xl border border-white/10 bg-black/25 p-3 flex items-center gap-3"
         data-testid={`affiliate-row-${kind}-${target.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold text-white truncate">{target.name}</div>
          {badge}
        </div>
        {subtitle && (
          <div className="text-[11px] text-white/50 truncate">{subtitle}</div>
        )}
        {membership && (
          <div className="text-[10px] text-white/40 font-mono">Membership # {membership}</div>
        )}
      </div>
      <a href={profileHref} target={kind === "org" ? "_blank" : undefined}
         rel="noreferrer"
         className="text-white/45 hover:text-white/85 p-1.5 rounded-md hover:bg-white/[0.05]"
         data-testid={`affiliate-profile-link-${target.id}`}
         title="Open profile">
        <ExternalLink size={13}/>
      </a>
      <div className="flex items-center gap-1.5">
        {status === "pending" && (
          <>
            <button type="button" disabled={busy}
              onClick={() => onVouch("accept")}
              className="px-2.5 py-1.5 rounded-md border border-emerald-500/45 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`vouch-accept-${target.id}`}>
              <Check size={11}/> Vouch
            </button>
            <button type="button" disabled={busy}
              onClick={() => onVouch("reject")}
              className="px-2.5 py-1.5 rounded-md border border-red-500/40 text-red-200 bg-red-500/8 hover:bg-red-500/15 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`vouch-reject-${target.id}`}>
              <X size={11}/> Reject
            </button>
          </>
        )}
        {status !== "pending" && (
          <button type="button" disabled={busy}
            onClick={() => onVouch("reset")}
            className="px-2.5 py-1.5 rounded-md border border-white/15 text-white/60 hover:bg-white/[0.05] text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`vouch-reset-${target.id}`}>
            <Undo2 size={11}/> Reset
          </button>
        )}
      </div>
    </div>
  );
}

// Silence lint when props destructuring covers `accent` unused paths.
DeclaredAffiliatesPanel.defaultProps = { accent: "#F5B82E" };
